import { z } from 'zod';
import { artifactClaimEvidenceSchema, } from './artifact-claim-evidence.schema.js';
import { specAgentDraftMetadataSchema } from './spec-agent-draft-metadata.schema.js';
const optionIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/);
const questionIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/);
/** Identifier-only client submission for one open-question answer. */
export const specificationOpenQuestionAnswerSubmissionSchema = z
    .object({
    question_id: questionIdSchema,
    selected_option_id: optionIdSchema.optional(),
    custom_answer: z.string().min(1).max(2000).optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    const hasOption = val.selected_option_id !== undefined;
    const hasCustom = val.custom_answer !== undefined;
    if (hasOption === hasCustom) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Provide exactly one of selected_option_id or custom_answer',
            path: hasOption ? ['custom_answer'] : ['selected_option_id'],
        });
    }
});
export const specificationOpenQuestionAnswersSubmissionSchema = z
    .array(specificationOpenQuestionAnswerSubmissionSchema)
    .max(6);
/** Canonical resolved pair injected into generation context. */
export const resolvedSpecOpenQuestionAnswerSchema = z
    .object({
    question_id: questionIdSchema,
    question: z.string().min(1).max(2048),
    answer: z.string().min(1).max(2000),
    selected_option_id: optionIdSchema.nullable(),
})
    .strict();
/** Trim and collapse whitespace. Exact match only — no fuzzy comparison. */
export function normalizeSpecOpenQuestionText(text) {
    return text.trim().replace(/\s+/g, ' ');
}
function readClarificationQuestionId(value) {
    if (!value)
        return null;
    return z.string().uuid().safeParse(value).success ? value : null;
}
const MAX_CANONICAL_OPEN_QUESTIONS = 6;
function isGuidedUncertainty(uncertainty) {
    return (typeof uncertainty.uncertainty_id === 'string' &&
        uncertainty.uncertainty_id.length > 0 &&
        Array.isArray(uncertainty.options) &&
        uncertainty.options.length >= 2 &&
        uncertainty.options.length <= 3 &&
        typeof uncertainty.recommended_option_id === 'string' &&
        uncertainty.options.some((option) => option.option_id === uncertainty.recommended_option_id));
}
/** Guided open questions are asked on the first specification version only. */
export const GUIDED_OPEN_QUESTIONS_VERSION_SEQUENCE = 1;
/**
 * True when a version's guided open questions are asked and answered (sequence 1). Later versions
 * neither show nor accept answers, on the server and on both pages.
 */
export function guidedOpenQuestionsApplyToSequence(sequence) {
    return sequence === GUIDED_OPEN_QUESTIONS_VERSION_SEQUENCE;
}
/** Extract guided-review open questions from persisted draft metadata. */
export function listCanonicalSpecOpenQuestionsFromMetadata(metadata) {
    const parsed = specAgentDraftMetadataSchema.safeParse(metadata);
    if (!parsed.success)
        return [];
    const evidence = parsed.data.artifact_claim_evidence;
    if (!evidence?.uncertainties?.length)
        return [];
    const questions = [];
    const seenText = new Set();
    for (const uncertainty of evidence.uncertainties) {
        if (!isGuidedUncertainty(uncertainty))
            continue;
        const textKey = normalizeSpecOpenQuestionText(uncertainty.text);
        if (seenText.has(textKey))
            continue;
        const recommendedId = uncertainty.recommended_option_id;
        const recommended = uncertainty.options.find((option) => option.option_id === recommendedId);
        const others = uncertainty.options.filter((option) => option.option_id !== recommendedId);
        if (!recommended)
            continue;
        seenText.add(textKey);
        const artifactSection = uncertainty.artifact_section?.trim() ?? '';
        questions.push({
            question_id: uncertainty.uncertainty_id,
            text: uncertainty.text,
            options: [
                { option_id: recommended.option_id, label: recommended.label, recommended: true },
                ...others.map((option) => ({
                    option_id: option.option_id,
                    label: option.label,
                    recommended: false,
                })),
            ],
            artifact_section: artifactSection.length > 0 ? artifactSection : null,
            clarification_question_id: readClarificationQuestionId(uncertainty.clarification_question_id),
        });
        if (questions.length >= MAX_CANONICAL_OPEN_QUESTIONS)
            break;
    }
    return questions;
}
/**
 * Validate identifier-only submissions against canonical draft questions and resolve labels.
 */
export function resolveSpecOpenQuestionAnswers(params) {
    const canonical = listCanonicalSpecOpenQuestionsFromMetadata(params.metadata);
    const byId = new Map(canonical.map((question) => [question.question_id, question]));
    if (params.submissions.length !== canonical.length) {
        return {
            ok: false,
            code: 'incomplete_answers',
            message: `Expected ${canonical.length} open-question answers, received ${params.submissions.length}`,
        };
    }
    const seen = new Set();
    const resolved = [];
    for (const submission of params.submissions) {
        if (seen.has(submission.question_id)) {
            return {
                ok: false,
                code: 'duplicate_question',
                message: `Duplicate answer for question ${submission.question_id}`,
            };
        }
        seen.add(submission.question_id);
        const question = byId.get(submission.question_id);
        if (!question) {
            return {
                ok: false,
                code: 'unknown_question',
                message: `Unknown open question id ${submission.question_id}`,
            };
        }
        if (submission.custom_answer !== undefined) {
            const custom = submission.custom_answer.trim();
            if (!custom) {
                return {
                    ok: false,
                    code: 'invalid_custom_answer',
                    message: `Custom answer for ${submission.question_id} is empty`,
                };
            }
            resolved.push({
                question_id: question.question_id,
                question: question.text,
                answer: custom,
                selected_option_id: null,
            });
            continue;
        }
        const option = question.options.find((entry) => entry.option_id === submission.selected_option_id);
        if (!option) {
            return {
                ok: false,
                code: 'unknown_option',
                message: `Unknown option ${submission.selected_option_id ?? ''} for question ${submission.question_id}`,
            };
        }
        resolved.push({
            question_id: question.question_id,
            question: question.text,
            answer: option.label,
            selected_option_id: option.option_id,
        });
    }
    for (const question of canonical) {
        if (!seen.has(question.question_id)) {
            return {
                ok: false,
                code: 'incomplete_answers',
                message: `Missing answer for question ${question.question_id}`,
            };
        }
    }
    return { ok: true, answers: resolved };
}
/** True when metadata has at least one guided open question. */
export function draftMetadataHasGuidedOpenQuestions(metadata) {
    return listCanonicalSpecOpenQuestionsFromMetadata(metadata).length > 0;
}
/** Parse claim evidence when only the evidence object is available. */
export function listCanonicalSpecOpenQuestionsFromClaimEvidence(claimEvidence) {
    const parsed = artifactClaimEvidenceSchema.safeParse(claimEvidence);
    if (!parsed.success)
        return [];
    return listCanonicalSpecOpenQuestionsFromMetadata({
        schema_version: 1,
        artifact_claim_evidence: parsed.data,
    });
}
//# sourceMappingURL=spec-open-question-answers.schema.js.map